import React, {useState} from 'react'
import {useSelector} from 'react-redux'
import {selectEffectiveRole} from '../../../../../app-store/selector'
import {Button} from '../../../../../core/shared/Button'
import {getPriceForCountry} from '../../../utils/getPriceForCountry'
import {PlanDynamicField} from '../PlanDynamicField'
import {
  getFieldsFromPlan,
  getDefaultValues,
  validatePlanFields,
} from '../planFieldsConfig'
import {
  FieldsGrid,
  AddToOrderRow,
  PanelContainer,
  EntitlementsList,
  EntitlementsTitle,
  EntitlementItem,
} from '../ProductPlanSelector.styles'

const FIELD_ORDER = ['domain', 'keapFreeTrial', 'keapFreeTrialAppId']

function sortFieldsForDisplay(fields) {
  return [...fields].sort((a, b) => {
    const orderA = FIELD_ORDER.indexOf(a.key)
    const orderB = FIELD_ORDER.indexOf(b.key)
    return (
      (orderA === -1 ? FIELD_ORDER.length : orderA) -
      (orderB === -1 ? FIELD_ORDER.length : orderB)
    )
  })
}

function isFieldVisible(field, values) {
  if (field.key === 'keapFreeTrialAppId') {
    return values.keapFreeTrial === 'yes'
  }
  return true
}

function validateFields(fields, values) {
  const errors = validatePlanFields(fields, values)
  // Only ask for the free-trial answer when the catalog defines that field for
  // the plan (it always does for 1:1). Flows selling to customers who don't
  // exist yet (1:Many links) strip the field and must not be blocked on it.
  const hasFreeTrialField = fields.some(f => f.key === 'keapFreeTrial')
  if (hasFreeTrialField && !values.keapFreeTrial) {
    errors.keapFreeTrial = 'Please select Yes or No'
  }
  if (
    hasFreeTrialField &&
    values.keapFreeTrial === 'yes' &&
    !String(values.keapFreeTrialAppId ?? '').trim()
  ) {
    errors.keapFreeTrialAppId = 'Keap Free Trial App ID is required'
  }
  return errors
}

function buildOrderConfiguration(values) {
  const config = {}
  if (values.domain) config.domain = values.domain
  if (values.keapFreeTrial) config.keapFreeTrial = values.keapFreeTrial
  if (values.keapFreeTrial === 'yes' && values.keapFreeTrialAppId) {
    config.keapFreeTrialAppId = values.keapFreeTrialAppId
  }
  return config
}

export function KeapAddonPanel({
  plan,
  selectedCountry,
  prodType,
  itemInOrder,
  groupPlanCodes,
  groupId,
  onAddProduct,
  onUpdateProduct,
  onClose,
}) {
  const allFields = sortFieldsForDisplay(getFieldsFromPlan(plan))
  const effectiveRole = useSelector(selectEffectiveRole)
  const canSeeCertPartEntitlement =
    effectiveRole.includes('Admin') || effectiveRole.includes('PartnerOps')
  const entitlements = (plan?.entitlements ?? []).filter(
    e =>
      e.code !== 'addon-5-keap-certpart-slp-m2m' || canSeeCertPartEntitlement,
  )
  const isItemInOrder = Boolean(itemInOrder)

  const [fieldValues, setFieldValues] = useState(() => ({
    ...getDefaultValues(allFields),
    ...itemInOrder?.configuredOptions,
  }))
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [selectedEntitlements, setSelectedEntitlements] = useState(() =>
    entitlements
      .filter(e => e.isRequired)
      .map(e => ({...e, quantity: e.quantity ?? 1})),
  )

  const visibleFields = allFields.filter(f => isFieldVisible(f, fieldValues))

  const handleFieldChange = (key, value) => {
    setFieldValues(prev => ({...prev, [key]: value}))
    setErrors(prev => ({...prev, [key]: null}))
  }

  const handleFieldBlur = key => {
    setTouched(prev => ({...prev, [key]: true}))
    const fieldErrors = validateFields(visibleFields, fieldValues)
    setErrors(prev => ({...prev, [key]: fieldErrors[key] ?? null}))
  }

  const handleEntitlementChange = (ent, checked) => {
    setSelectedEntitlements(prev =>
      checked
        ? [...prev, {...ent, quantity: ent.quantity ?? 1}]
        : prev.filter(e => e.code !== ent.code),
    )
  }

  const handleAddToOrder = () => {
    const fieldErrors = validateFields(visibleFields, fieldValues)
    const allTouched = visibleFields.reduce(
      (acc, f) => ({...acc, [f.key]: true}),
      {},
    )
    setTouched(prev => ({...prev, ...allTouched}))
    setErrors(prev => ({...prev, ...fieldErrors}))
    if (Object.keys(fieldErrors).length > 0) return

    const product = {
      productId: plan.code,
      productCode: plan.code,
      name: plan.name,
      price: getPriceForCountry(plan.currencies, selectedCountry),
      quantity: 1,
      type: prodType === 'Center' ? 'software' : 'addon',
      billingFrequency:
        plan.productBillingType === 'Annual' ? 'annual' : 'monthly',
      configuredOptions: buildOrderConfiguration(fieldValues),
      addons: selectedEntitlements,
      excludes: plan.excludes ?? [],
      autoAddCenters: plan.autoAddCenters ?? [],
      disableApps: plan.disableApps ?? [],
      disableCenters: plan.disableCenters ?? [],
      maxQty: plan.maxQty ?? null,
      productRank: plan.productRank ?? null,
      downgrades: plan.downgrades ?? [],
      parentProduct: plan.parentProduct,
    }

    if (isItemInOrder) {
      ;(onUpdateProduct ?? (p => onAddProduct(p, groupPlanCodes, groupId)))(
        product,
      )
    } else {
      onAddProduct(product, groupPlanCodes, groupId)
    }
    onClose()
  }

  return (
    <PanelContainer>
      <FieldsGrid>
        {visibleFields.map(field => (
          <PlanDynamicField
            key={field.key}
            field={field}
            value={fieldValues[field.key] ?? ''}
            values={fieldValues}
            error={touched[field.key] ? errors[field.key] : null}
            touched={touched[field.key]}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            variant="form"
            size="md"
          />
        ))}
      </FieldsGrid>
      {entitlements.length > 0 && (
        <>
          <EntitlementsTitle>Entitlements</EntitlementsTitle>
          <EntitlementsList>
            {entitlements.map(ent => (
              <EntitlementItem key={ent.code}>
                <label style={{cursor: ent.isRequired ? 'default' : 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={
                      ent.isRequired ||
                      selectedEntitlements.some(e => e.code === ent.code)
                    }
                    disabled={ent.isRequired}
                    onChange={e =>
                      handleEntitlementChange(ent, e.target.checked)
                    }
                  />{' '}
                  {ent.name}
                </label>
              </EntitlementItem>
            ))}
          </EntitlementsList>
        </>
      )}
      <AddToOrderRow>
        <Button
          type="button"
          variant="action-orange"
          size="sm"
          onClick={handleAddToOrder}
        >
          {isItemInOrder ? 'Update Order' : 'Add to Order'}
        </Button>
      </AddToOrderRow>
    </PanelContainer>
  )
}
